import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import {
  useProduct,
  useProductPublication,
  useSearchCategories,
  useUpdateProductState,
  useUpsertProduct,
} from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { formatPrice } from '../../lib/format';
import { computeReadiness } from '../../lib/readiness';
import { useToast } from '../../lib/useToast';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  COMMERCIAL_AVAILABILITY_OPTIONS,
  STOCK_STATUS_OPTIONS,
} from '../../model/options';
import type {
  CommercialAvailability,
  ProductAttribute,
  ProductUpsertRequest,
  StockStatus,
} from '../../model/types';
import { MediaManager } from '../components/MediaManager';
import { ProductPublicationCard } from '../components/ProductPublicationCard';
import { ReadinessChecklist } from '../components/ReadinessChecklist';

// ── Local types ──────────────────────────────────────────────────────────────

interface ProductDraft {
  name: string;
  code: string;
  categoryId: string;
  categoryName: string;
  brand: string;
  unit: string;
  price: string;
  oldPrice: string;
  stockStatus: StockStatus;
  description: string;
  attributes: ProductAttribute[];
  images: string[];
  commercialAvailability: CommercialAvailability;
}

const EMPTY_DRAFT: ProductDraft = {
  name: '',
  code: '',
  categoryId: '',
  categoryName: '',
  brand: '',
  unit: '',
  price: '',
  oldPrice: '',
  stockStatus: 'CHECK_AVAILABILITY',
  description: '',
  attributes: [],
  images: [],
  commercialAvailability: 'AVAILABLE',
};

// ── Customer state preview ────────────────────────────────────────────────────

/** Human-readable labels for the backend-computed CustomerState.state values. */
const CUSTOMER_STATE_LABELS: Record<string, string> = {
  AVAILABLE: 'Доступен к покупке',
  PREORDER: 'Предзаказ',
  CHECK_AVAILABILITY: 'Уточняйте наличие',
  COMING_SOON: 'Скоро в продаже',
  OUT_OF_STOCK: 'Нет в наличии',
  TEMPORARILY_UNAVAILABLE: 'Временно недоступен',
  NO_PRICE: 'Цена недоступна',
  UNAVAILABLE: 'Недоступен',
};

/**
 * Compact read-only panel showing the backend-computed buyer state.
 * Placed inside the hero card so merchants see it alongside the readiness checklist.
 */
function CustomerStatePreview({
  state,
  orderable,
  showPrice,
  reasonCode,
}: {
  state: string;
  orderable: boolean;
  showPrice: boolean;
  reasonCode?: string | null;
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: orderable ? 'success.50' : 'action.hover',
        border: '1px solid',
        borderColor: orderable ? 'success.200' : 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        Текущее состояние для покупателя
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={CUSTOMER_STATE_LABELS[state] ?? state}
          color={orderable ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip
          size="small"
          label={orderable ? 'Можно заказать' : 'Нельзя заказать'}
          color={orderable ? 'success' : 'error'}
          variant={orderable ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={showPrice ? 'Цена видна' : 'Цена скрыта'}
          color={showPrice ? 'success' : 'default'}
          variant="outlined"
        />
        {reasonCode ? (
          <Typography variant="caption" color="text.secondary">
            Причина: {reasonCode}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function newProductId(): string {
  // crypto.randomUUID() is available in all modern browsers (Chrome 92+, Firefox 95+, Safari 15.4+).
  // No fallback — a non-UUID string would fail backend UUID validation on the product endpoint.
  return crypto.randomUUID();
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Create or edit a product (PUT upsert). Handles attributes + photo gallery.
 *
 * ## Layout hierarchy
 *
 * The page uses full available width (minus sidebar) and is structured to answer
 * "is this product ready to sell?" at a glance:
 *
 * 1. **Top zone — 3-column grid (primary, above the fold)**
 *    - Col 1: Primary photo (objectFit: contain — no cropping) + gallery management
 *    - Col 2: Identity (name, code, brand, category, price) + Readiness checklist
 *              (live-updating as you edit price/availability) + CustomerState
 *    - Col 3: Коммерческая доступность selector + Publication card
 *              (channel state + publish/unpublish/archive actions)
 *
 * 2. **Form sections (secondary — scroll to reach)**
 *    - Основная информация
 *    - Цена и наличие
 *    - Описание
 *    - Характеристики
 */
export function ProductEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const productId = params.productId && params.productId !== 'new' ? params.productId : undefined;
  const isEdit = Boolean(productId);

  const { canView, canManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const productQuery = useProduct(productId, { enabled: canView && isEdit });
  const publicationQuery = useProductPublication(productId, { enabled: canView && isEdit });
  const categoriesQuery = useSearchCategories({ page: 0, size: 100, sort: 'name,asc' }, { enabled: canView });
  const upsert = useUpsertProduct();
  const updateState = useUpdateProductState();

  const [draft, setDraft] = useState<ProductDraft>(EMPTY_DRAFT);
  const setField = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Seed the draft from the loaded product (edit) once.
  const loaded = productQuery.data;
  useEffect(() => {
    if (!loaded) return;
    setDraft({
      name: loaded.name ?? '',
      code: loaded.code ?? '',
      categoryId: loaded.categoryId ?? '',
      categoryName: loaded.categoryName ?? '',
      brand: loaded.brand ?? '',
      unit: loaded.unit ?? '',
      price: loaded.price != null ? String(loaded.price) : '',
      oldPrice: loaded.oldPrice != null ? String(loaded.oldPrice) : '',
      stockStatus: loaded.stockStatus ?? 'CHECK_AVAILABILITY',
      description: loaded.description ?? '',
      attributes: loaded.attributes ?? [],
      images: loaded.images ?? (loaded.imageUrl ? [loaded.imageUrl] : []),
      commercialAvailability: loaded.commercialAvailability ?? 'AVAILABLE',
    });
  }, [loaded]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const priceInput = draft.price.trim();
  const priceNum = priceInput === '' ? 0 : Number(priceInput);
  const priceValid = priceInput === '' || (!Number.isNaN(priceNum) && priceNum >= 0);
  const valid = draft.name.trim() !== '' && draft.categoryId !== '' && priceValid;
  const disabled = !canManage;

  const categoryOptions = useMemo(() => categoriesQuery.data?.content ?? [], [categoriesQuery.data]);

  // Primary image (hero thumbnail) — first uploaded image or the loaded cover.
  const primaryImage = draft.images[0] ?? undefined;

  // Publication state (for readiness panel + hero image).
  const pubData = publicationQuery.data;
  const pubChannels = pubData?.channels ?? [];
  const publishedChannelCodes = pubChannels
    .filter((c) => c.publicationStatus === 'PUBLISHED')
    .map((c) => c.channelCode);
  const isPublishedAnywhere = publishedChannelCodes.length > 0;

  /**
   * Live-computed readiness: feeds off the current form state so the checklist
   * updates in real-time as the merchant changes price or commercial availability,
   * even before saving.
   */
  const readiness = useMemo(
    () =>
      computeReadiness({
        // cardStatus comes from the API (not a form field) — fall back to ACTIVE (backend default).
        cardStatus: pubData?.cardStatus ?? loaded?.cardStatus,
        // commercialAvailability is a form field — use the live draft value.
        commercialAvailability: draft.commercialAvailability,
        published: isPublishedAnywhere,
        publishedChannels: publishedChannelCodes,
        // price is a form field — use the live draft value.
        price: priceInput !== '' ? priceNum : undefined,
      }),
    [
      pubData?.cardStatus,
      loaded?.cardStatus,
      draft.commercialAvailability,
      isPublishedAnywhere,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      publishedChannelCodes.join(','),
      priceInput,
      priceNum,
    ],
  );

  /**
   * Warn when stockStatus and commercialAvailability send contradictory signals.
   * The backend uses commercialAvailability as the authoritative buyer-state source
   * and ignores stockStatus entirely when it is set.
   */
  const stockContradictionWarning = useMemo<string | null>(() => {
    const stock = draft.stockStatus;
    const commercial = draft.commercialAvailability;
    if (
      stock === 'IN_STOCK' &&
      (commercial === 'OUT_OF_STOCK' || commercial === 'TEMPORARILY_UNAVAILABLE')
    ) {
      return `Поле «Наличие» указывает «В наличии», но «Коммерческая доступность» (${COMMERCIAL_AVAILABILITY_LABELS[commercial] ?? commercial}) блокирует покупку на витрине. Покупатель увидит товар как недоступный.`;
    }
    if (stock === 'OUT_OF_STOCK' && commercial === 'AVAILABLE') {
      return 'Поле «Наличие» указывает «Нет в наличии», но «Коммерческая доступность» разрешает покупку. Покупатель увидит товар как доступный, хотя склад пуст.';
    }
    return null;
  }, [draft.stockStatus, draft.commercialAvailability]);

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!valid) return;
    const body: ProductUpsertRequest = {
      name: draft.name.trim(),
      code: draft.code.trim() || undefined,
      categoryId: draft.categoryId,
      categoryName: draft.categoryName,
      brand: draft.brand.trim() || undefined,
      unit: draft.unit.trim() || undefined,
      price: priceInput === '' ? 0 : priceNum,
      oldPrice: draft.oldPrice.trim() !== '' ? Number(draft.oldPrice) : undefined,
      stockStatus: draft.stockStatus,
      description: draft.description.trim() || undefined,
      imageUrl: draft.images[0],
      images: draft.images.length ? draft.images : undefined,
      attributes: draft.attributes.filter((a) => a.name.trim() && a.value.trim()),
    };
    // `id` is the resolved product UUID — either the existing one (edit) or a freshly
    // generated UUID (create). Using `id` (not `productId`) in both mutations ensures
    // commercialAvailability is persisted on first save too, not just on subsequent edits.
    const id = productId ?? newProductId();
    upsert.mutate(
      { productId: id, body },
      {
        onSuccess: async () => {
          try {
            await updateState.mutateAsync({
              productId: id,
              body: { commercialAvailability: draft.commercialAvailability },
            });
            success(isEdit ? 'Товар сохранён.' : 'Товар создан.');
            navigate('/catalog/products');
          } catch (mutationError) {
            notifyError(
              mutationError instanceof Error
                ? mutationError.message
                : 'Не удалось сохранить статус товара.',
            );
          }
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  // ── Guard states ───────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <PageContainer title="Товар">
        <ErrorState title="Доступ ограничен" message="У вас нет прав на просмотр каталога." />
      </PageContainer>
    );
  }
  if (isEdit && productQuery.isLoading) {
    return (
      <PageContainer title="Товар">
        <LoadingState label="Загрузка товара…" />
      </PageContainer>
    );
  }
  if (isEdit && productQuery.isError) {
    return (
      <PageContainer title="Товар">
        <ErrorState
          title="Не удалось загрузить товар"
          message={productQuery.error?.message ?? 'Произошла ошибка.'}
          onRetry={() => void productQuery.refetch()}
        />
      </PageContainer>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageContainer
      title={isEdit ? draft.name || 'Товар' : 'Новый товар'}
      subtitle={isEdit ? 'Редактирование карточки товара.' : 'Создание новой карточки товара.'}
      maxWidth="100%"
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/catalog/products')}
          >
            Назад
          </Button>
          {canManage ? (
            <Button
              variant="contained"
              startIcon={
                upsert.isPending || updateState.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              disabled={!valid || upsert.isPending || updateState.isPending}
              onClick={handleSave}
            >
              Сохранить
            </Button>
          ) : null}
        </Stack>
      }
    >
      <Stack spacing={2.5}>

        {/* ═══════════════════════════════════════════════════════════════════
            TOP ZONE — 3-COLUMN LAYOUT, PRIMARY CONTENT ABOVE THE FOLD
            Col 1: Photo (no crop) + gallery   Col 2: Identity + Readiness
            Col 3: Коммерческая доступность + Publication card
            ═══════════════════════════════════════════════════════════════ */}
        <Grid container spacing={2.5} alignItems="flex-start">

          {/* ── Col 1: Primary photo + gallery management ──────────────── */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent
                sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}
              >
                {/* Primary photo — objectFit: contain so the full image is always visible */}
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '4 / 3',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {primaryImage ? (
                    <Box
                      component="img"
                      src={primaryImage}
                      alt={draft.name || 'Товар'}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Inventory2RoundedIcon sx={{ fontSize: 64, color: 'action.disabled' }} />
                  )}
                </Box>

                {/* Gallery management — right below the primary photo */}
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
                >
                  Галерея фотографий
                </Typography>
                <MediaManager
                  ownerId={productId}
                  images={draft.images}
                  onChange={(images) => setField('images', images)}
                  disabled={disabled}
                  onResult={(severity, message) =>
                    severity === 'success' ? success(message) : notifyError(message)
                  }
                />
              </CardContent>
            </Card>
          </Grid>

          {/* ── Col 2: Identity + Readiness checklist + Buyer state ─────── */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent
                sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}
              >
                <Stack spacing={2}>
                  {/* Product identity — updates in real-time as the form below is edited */}
                  <Box>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        lineHeight: 1.2,
                        mb: 0.5,
                        color: draft.name.trim() ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {draft.name.trim() || 'Название товара'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[
                        draft.code && `Арт. ${draft.code}`,
                        draft.brand,
                        draft.categoryName,
                      ]
                        .filter(Boolean)
                        .join(' · ') || (isEdit ? '' : 'Заполните основную информацию ниже')}
                    </Typography>
                    {isEdit && (
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={formatPrice(priceNum > 0 ? priceNum : undefined)}
                          color={priceNum > 0 ? 'default' : 'warning'}
                        />
                      </Stack>
                    )}
                  </Box>

                  <Divider />

                  {/* Readiness checklist — LIVE-UPDATING as form fields change */}
                  <Box>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
                    >
                      Готовность к продаже
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      {isEdit && publicationQuery.isLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Проверка статусов…
                          </Typography>
                        </Stack>
                      ) : (
                        <ReadinessChecklist readiness={readiness} />
                      )}
                    </Box>
                  </Box>

                  {/* Backend-computed buyer state (edit only, after load) */}
                  {isEdit && loaded?.customerState ? (
                    <>
                      <Divider />
                      <CustomerStatePreview
                        state={loaded.customerState.state}
                        orderable={loaded.customerState.orderable}
                        showPrice={loaded.customerState.showPrice}
                        reasonCode={loaded.customerState.reasonCode}
                      />
                    </>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Col 3: Commercial availability + Publication card ─────── */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2.5}>
              {/* Коммерческая доступность */}
              <Card variant="outlined">
                <CardContent
                  sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}
                >
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Коммерческая доступность
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
                      Статус влияет на то, можно ли купить товар и какое сообщение видит покупатель
                      на витрине. Это поле приоритетнее поля «Наличие».
                    </Alert>
                    <TextField
                      select
                      label="Коммерческая доступность"
                      value={draft.commercialAvailability}
                      onChange={(e) =>
                        setField('commercialAvailability', e.target.value as CommercialAvailability)
                      }
                      disabled={disabled || updateState.isPending}
                      fullWidth
                    >
                      {COMMERCIAL_AVAILABILITY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </CardContent>
              </Card>

              {/* Publication card — channel state + publish / unpublish / archive actions */}
              {isEdit && productId ? (
                <ProductPublicationCard
                  productId={productId}
                  onResult={(severity, message) =>
                    severity === 'success' ? success(message) : notifyError(message)
                  }
                />
              ) : null}
            </Stack>
          </Grid>

        </Grid>

        {/* View-only notice */}
        {disabled ? (
          <Alert severity="info">
            У вас нет прав на изменение каталога — карточка открыта в режиме просмотра.
          </Alert>
        ) : null}

        {/* ═══════════════════════════════════════════════════════════════════
            FORM SECTIONS — SECONDARY CONTENT (scroll to reach)
            Ordered by relevance to publication readiness:
             1. Основная информация — name/code/brand/category shown in hero
             2. Цена и наличие — directly affects the Цена gate
             3. Описание — buyer-facing secondary content
             4. Характеристики — buyer-facing secondary content
            ═══════════════════════════════════════════════════════════════ */}

        {/* 1. Basic product information */}
        <Section title="Основная информация">
          <Stack spacing={2.5}>
            <TextField
              label="Название"
              required
              value={draft.name}
              onChange={(e) => setField('name', e.target.value)}
              error={!disabled && draft.name.trim() === ''}
              disabled={disabled}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label="Артикул (SKU)"
                value={draft.code}
                onChange={(e) => setField('code', e.target.value)}
                disabled={disabled}
                fullWidth
              />
              <TextField
                label="Бренд"
                value={draft.brand}
                onChange={(e) => setField('brand', e.target.value)}
                disabled={disabled}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                select
                label="Категория"
                required
                value={draft.categoryId}
                onChange={(e) => {
                  const cat = categoryOptions.find((c) => c.id === e.target.value);
                  setDraft((d) => ({
                    ...d,
                    categoryId: e.target.value,
                    categoryName: cat?.name ?? d.categoryName,
                  }));
                }}
                error={!disabled && draft.categoryId === ''}
                disabled={disabled}
                fullWidth
                helperText={
                  categoryOptions.length === 0 ? 'Сначала создайте категорию.' : undefined
                }
              >
                {draft.categoryId && !categoryOptions.some((c) => c.id === draft.categoryId) ? (
                  <MenuItem value={draft.categoryId}>
                    {draft.categoryName || draft.categoryId}
                  </MenuItem>
                ) : null}
                {categoryOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Единица измерения"
                placeholder="шт., кг, л…"
                value={draft.unit}
                onChange={(e) => setField('unit', e.target.value)}
                disabled={disabled}
                fullWidth
              />
            </Stack>
          </Stack>
        </Section>

        {/* 2. Price & stock — readiness gate #4 (price) */}
        <Section title="Цена и наличие">
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label="Цена, ₽"
                type="number"
                value={draft.price}
                onChange={(e) => setField('price', e.target.value)}
                error={!disabled && !priceValid}
                disabled={disabled}
                fullWidth
                slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                helperText="Можно оставить пустым или указать 0."
              />
              <TextField
                label="Старая цена, ₽"
                type="number"
                value={draft.oldPrice}
                onChange={(e) => setField('oldPrice', e.target.value)}
                disabled={disabled}
                fullWidth
                slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                helperText="Для зачёркнутой цены"
              />
            </Stack>
            <Stack spacing={1}>
              <TextField
                select
                label="Наличие"
                value={draft.stockStatus}
                onChange={(e) => setField('stockStatus', e.target.value as StockStatus)}
                disabled={disabled}
                sx={{ maxWidth: { sm: 280 } }}
                fullWidth
                helperText="Запасное значение — используется на витрине только если «Коммерческая доступность» не задана явно."
              >
                {STOCK_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              {stockContradictionWarning ? (
                <Alert severity="warning">{stockContradictionWarning}</Alert>
              ) : null}
            </Stack>
          </Stack>
        </Section>

        {/* 3. Description */}
        <Section title="Описание">
          <TextField
            label="Описание товара"
            value={draft.description}
            onChange={(e) => setField('description', e.target.value)}
            disabled={disabled}
            fullWidth
            multiline
            minRows={3}
          />
        </Section>

        {/* 4. Attributes */}
        <Section title="Характеристики">
          <Stack spacing={1.5}>
            {draft.attributes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Характеристики не заданы.
              </Typography>
            ) : null}
            {draft.attributes.map((attr, index) => (
              <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                <TextField
                  label="Название"
                  size="small"
                  value={attr.name}
                  onChange={(e) =>
                    setDraft((d) => {
                      const next = [...d.attributes];
                      next[index] = { ...next[index], name: e.target.value };
                      return { ...d, attributes: next };
                    })
                  }
                  disabled={disabled}
                  fullWidth
                />
                <TextField
                  label="Значение"
                  size="small"
                  value={attr.value}
                  onChange={(e) =>
                    setDraft((d) => {
                      const next = [...d.attributes];
                      next[index] = { ...next[index], value: e.target.value };
                      return { ...d, attributes: next };
                    })
                  }
                  disabled={disabled}
                  fullWidth
                />
                <IconButton
                  aria-label="Удалить характеристику"
                  disabled={disabled}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      attributes: d.attributes.filter((_, i) => i !== index),
                    }))
                  }
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            {!disabled ? (
              <Box>
                <Button
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      attributes: [...d.attributes, { name: '', value: '' }],
                    }))
                  }
                >
                  Добавить характеристику
                </Button>
              </Box>
            ) : null}
          </Stack>
        </Section>

      </Stack>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
